import {
  AssetPath,
  BAKED_ASSETS_MANIFEST_REL_PATH,
  BakedAssetManifestIndex,
  isDistStorageRoot,
  parseBakedAssetsManifest,
  parseJson,
  PROJECT_PATH_PREFIX,
  remapLogicalAssetPath,
} from '@gnsx/genesys.js';

import type { IStorageProvider } from './interfaces';

/** True when this scoped provider reads from a built `.dist` output folder. */
export function isDistScopedRoot(rootPath: string): boolean {
  if (!rootPath) {
    return false;
  }
  return isDistStorageRoot(rootPath.replace(/\\/g, '/'));
}

const MANIFEST_DECORATED_PATH = `${PROJECT_PATH_PREFIX}/${BAKED_ASSETS_MANIFEST_REL_PATH}`;

/**
 * Lazy loader for `.dist/.genesys/baked-assets.json` via an underlying provider.
 * Used by {@link ScopedStorageProvider} when scoped to dist (PIE, published play).
 */
export class ScopedBakedAssetManifestLoader {
  private index: BakedAssetManifestIndex | null | undefined;
  private loadPromise: Promise<BakedAssetManifestIndex | null> | null = null;

  constructor(
    private readonly rootPath: string,
    private readonly underlyingProvider: IStorageProvider,
    private readonly extractFilePath: (decoratedPath: string) => string,
  ) {}

  public async getIndex(): Promise<BakedAssetManifestIndex | null> {
    if (!isDistScopedRoot(this.rootPath)) {
      return null;
    }
    if (this.index !== undefined) {
      return this.index;
    }
    if (!this.loadPromise) {
      this.loadPromise = this.loadFromDist();
    }
    this.index = await this.loadPromise;
    return this.index;
  }

  public async remapDecoratedPath(decoratedPath: string): Promise<string> {
    if (!decoratedPath.startsWith(PROJECT_PATH_PREFIX)) {
      return decoratedPath;
    }
    const index = await this.getIndex();
    return remapLogicalAssetPath(decoratedPath, index);
  }

  private async loadFromDist(): Promise<BakedAssetManifestIndex | null> {
    try {
      const filePath = this.extractFilePath(MANIFEST_DECORATED_PATH);
      const manifestPath = AssetPath.fromString(filePath);
      // Absent manifest is normal when asset baking is disabled or no textures were
      // remapped — avoid a 404 download (and file-server error noise) on first play.
      if (!(await this.underlyingProvider.exists(manifestPath))) {
        return null;
      }
      const content = await this.underlyingProvider.downloadFileAsText(manifestPath);
      const manifest = parseBakedAssetsManifest(parseJson(content));
      if (!manifest || Object.keys(manifest.mappings).length === 0) {
        return null;
      }
      return BakedAssetManifestIndex.fromManifest(manifest);
    } catch {
      return null;
    }
  }
}
